import {
    CustomProtocol,
    CustomProtocolService,
    FilterBuilder,
    FilterPage,
    FilterSort,
    Status,
    translateCommonErrorStatus,
    DetailRequest,
    OrganizationProtocolPreference,
    ProtocolFilter,
} from '@core/data';
import {Action, State, StateContext, Selector} from '@ngxs/store';
import {Observable, forkJoin, of} from 'rxjs';
import {catchError, switchMap, tap} from 'rxjs/operators';
import {LocalStorage} from '@core/browser';
import { SetOrganizationContext } from '@app/auth';
import { Injectable } from '@angular/core';
import { Protocol, SampleType } from '@library';
import * as Fuse from 'fuse.js';
import { OrganizationProtocolPreferencesService } from '@core/data/api/organization-protocol-preferences.service';
import * as moment from 'moment';


export interface CustomProtocolIndexStateModel {

    // org context
    orgKey: string;

    // table data
    status: Status;
    data: Protocol[];
    preferences: OrganizationProtocolPreference[];
    customProtocol: CustomProtocol;
    total: number;

    // filter options
    page: FilterPage;
    sort: FilterSort;
    query: CustomProtocolIndexQuery;

    // table options
    displayColumns: string[];
    sortOptions: { id: string, label: string }[];
    pageSizeOptions: number[];

    // export result
    exports: {
        status: Status;
    }[];

    // selection
    selectedIds: string[];
    selectedData: Protocol[];
}

export interface CustomProtocolIndexQuery {
    title: string[];
    search: string;
    baseProtocols: boolean;
    archived: boolean;
    crop: string[];
    sampleType: string[];
    hidden: boolean;
    favourite: boolean;
}

const DEFAULTS: CustomProtocolIndexStateModel = {
    orgKey: null,
    status: Status.UNINITIALIZED,
    data: [],
    preferences: [],
    customProtocol: null,
    total: 0,
    page: {
        index: 0,
        size: 20
    },
    sort: {
        column: 'title',
        order: 'asc'
    },
    displayColumns: [
        'selectionBox',
        'title',
        'actions',
        'crop',
        'sampleType',
        'chars',
        'measures',
        'indexes',
        'timestamps',
    ],
    //? SORT OPTIONS MUST MATCH PROTOCOL PROPERTIES
    sortOptions: [
        { id: 'title', label: 'Title' },
        { id: 'chars', label: 'Characteristic Count' },
        { id: 'measures', label: 'Measurement Count' },
        { id: 'indexes', label: 'Index Count' },
        { id: 'updatedAt', label: 'Updated Date' },
        { id: 'createdAt', label: 'Created Date' },
    ],
    pageSizeOptions: [20, 50, 100, 500, 1000],
    query: {
        title: null,
        search: null,
        baseProtocols: false,
        archived: false,
        crop: [],
        sampleType: [SampleType.PLANT, SampleType.HARVEST],
        hidden: false,
        favourite: true,
    },
    exports: [],
    selectedIds: [],
    selectedData: [],
};

export class InitCustomProtocolIndex {
    static readonly type = '[CustomProtocolIndex] Init';

    constructor(public orgKey: string) {
    }
}

export class LoadCustomProtocolIndex {
    static readonly type = '[CustomProtocolIndex] Load';

    constructor() {
    }
}

export class AddCustomProtocol {
    static readonly type = '[CustomProtocolIndex] Add';

    constructor(
        public orgKey: string,
        public data: Partial<CustomProtocol>,
    ) {}
}

export class RemoveCustomProtocol {
    static readonly type = '[CustomProtocolIndex] Remove';

    constructor(public key: string) {
    }
}

export class PageCustomProtocolIndex {
    static readonly type = '[CustomProtocolIndex] Page';

    constructor(public index: number, public size: number) {
    }
}

export class SortCustomProtocolIndex {
    static readonly type = '[CustomProtocolIndex] Sort';

    constructor(public column: string, public order = 'asc') {
    }
}

export class FilterCustomProtocolIndex {
    static readonly type = '[CustomProtocolIndex] Filter';

    constructor(public query: CustomProtocolIndexQuery) {
    }
}

export class ResetCustomProtocolIndex {
    static readonly type = '[CustomProtocolIndex] Reset';

    constructor() {
    }
}

export class ReadCustomProtocol {
    static readonly type = '[CustomProtocolIndex] Read';
    constructor(public key: string) {}
}

export class LoadProtocolPreference {
    static readonly type = '[CustomProtocolIndex] Load Preference';
    constructor(public orgKey: string) {}
}

export class AddProtocolPreference {
    static readonly type = '[CustomProtocolIndex] Add Preference';
    constructor(public orgKey: string, public protocol: Protocol) {}
}

export class AddPreferenceWithoutProtocolId {
    static readonly type = '[CustomProtocolIndex] Add Preference without protocolId';
    constructor(public preference: Partial<OrganizationProtocolPreference>) {}
}

export class SetCustomProtocolIndexSelected {
    static readonly type = "[CustomProtocolIndex] Set Selected";
    constructor(public ids: string[]) {}
}

@State<CustomProtocolIndexStateModel>({
    name: 'custom_protocol_index',
    defaults: DEFAULTS
})
@Injectable()
export class CustomProtocolState {
    private fuse: Fuse<{}, any> | null = null;

    @Selector()
    static query(state: CustomProtocolIndexStateModel) {
        return state.query;
    }

    @Selector()
    static sort(state: CustomProtocolIndexStateModel) {
        return state.sort;
    }

    @Selector()
    static customProtocol(state: CustomProtocolIndexStateModel) {
        return state.customProtocol;
    }

    constructor(
        private _protocol_preferencesService: OrganizationProtocolPreferencesService,
        private _custom_protocolService: CustomProtocolService,
        private _storage: LocalStorage,
    ) {
    }

    @Action(InitCustomProtocolIndex)
    initCustomProtocolIndex(ctx: StateContext<CustomProtocolIndexStateModel>, action: InitCustomProtocolIndex) {

        ctx.patchState({
            ...DEFAULTS,
            orgKey: action.orgKey,
            data: [],
            total: 0,
            exports: []
        });


        this.restoreFilter(ctx);

        return ctx.dispatch(new LoadProtocolPreference(action.orgKey));
    }

    @Action(LoadCustomProtocolIndex, {cancelUncompleted: true})
    loadCustomProtocolIndex(ctx: StateContext<CustomProtocolIndexStateModel>, action: LoadCustomProtocolIndex) {

        const state = ctx.getState();

        if (!state.orgKey) {
            console.warn('Attempting to load CustomProtocolIndex without organization key.');
        }

        const detail: DetailRequest = {
            counts: []
        };

        const filter: Partial<ProtocolFilter> = {
            showFavourites: state.query.favourite,
            showHidden: state.query.hidden,
            crops: state.query.crop,
            sampleTypes: state.query.sampleType,
            baseProtocols: state.query.baseProtocols,
        }

        this._custom_protocolService.allProtocolsObserved(state.orgKey, state.preferences, filter).subscribe(all => {
            let result: Protocol[] = all;

            //? SEARCH
            if (state.query.search) {
                this.fuse = new Fuse(result, {
                    keys: ['title'],
                    threshold: 0.4 //? How loose the searching should be, lower is stricter
                });

                result = this.fuse.search(state.query.search) as Protocol[]
            }

            //? SORT
            if (result.length > 0) {
                const isDateSort = (state.sort.column == 'createdAt' || state.sort.column == 'updatedAt');
                const control = result[0][state.sort.column];
                if (Array.isArray(control)) result = this.sortCountColumn(state, result);
                if (isDateSort) result = this.sortTimestampColumn(state, result);
                if (typeof control === 'string' && !isDateSort) result = this.sortStringColumn(state, result);
            }

            //? PAGINATE
            const fullSize = result.length;
            result = this.paginateProtocols(state, result);

            if (result.length === 0) {
                return ctx.patchState({
                    status: Status.EMPTY,
                    total: 0,
                    data: []
                });
            }

            this.storeFilter(ctx);

            ctx.patchState({
                status: Status.OK,
                total: fullSize,
                data: result
            });
        })

    }


    @Action(PageCustomProtocolIndex)
    pageCustomProtocolIndex(ctx: StateContext<CustomProtocolIndexStateModel>, action: PageCustomProtocolIndex) {
        const state = ctx.getState()

        ctx.patchState({
            page: {
                index: action.index,
                size: action.size
            }
        });

        return ctx.dispatch(new LoadProtocolPreference(state.orgKey));

    }

    @Action(SortCustomProtocolIndex)
    sortCustomProtocolIndex(ctx: StateContext<CustomProtocolIndexStateModel>, action: SortCustomProtocolIndex) {

        const state = ctx.getState();

        ctx.patchState({
            sort: {
                column: action.column,
                order: action.order
            },
            page: {
                index: 0,
                size: state.page.size
            }
        });

        return ctx.dispatch(new LoadProtocolPreference(state.orgKey));

    }

    @Action(FilterCustomProtocolIndex)
    filterCustomProtocolIndex(ctx: StateContext<CustomProtocolIndexStateModel>, action: FilterCustomProtocolIndex) {

        const state = ctx.getState();

        ctx.patchState({
            query: action.query,
            page: {
                index: 0,
                size: state.page.size
            }
        });

        return ctx.dispatch(new LoadProtocolPreference(state.orgKey));

    }

    @Action(ResetCustomProtocolIndex)
    resetCustomProtocolIndex(ctx: StateContext<CustomProtocolIndexStateModel>, action: FilterCustomProtocolIndex) {

        const state = ctx.getState();

        ctx.patchState({
            query: DEFAULTS.query,
            page: DEFAULTS.page,
            sort: DEFAULTS.sort
        });

        return ctx.dispatch(new LoadProtocolPreference(state.orgKey));

    }

    @Action(AddCustomProtocol)
    addCustomProtocol(ctx: StateContext<CustomProtocolIndexStateModel>, action: AddCustomProtocol) {
        let request: Observable<CustomProtocol>;

        if (action.data.key) request = this._custom_protocolService.update(action.data.key, action.data);
        else request = this._custom_protocolService.create(action.data);

        return request.pipe(
            tap(result => {
                ctx.patchState({
                    status: Status.OK,
                });
                return forkJoin(
                    ctx.dispatch(new LoadProtocolPreference(action.orgKey)),
                    ctx.dispatch(new SetOrganizationContext(action.orgKey)),
                );
            })
        );
    }

    @Action(ReadCustomProtocol)
    readCustomProtocol(ctx: StateContext<CustomProtocolIndexStateModel>, action: ReadCustomProtocol) {
        ctx.patchState({
            status: Status.LOADING,
            customProtocol: null,
        });

        return this._custom_protocolService.read(action.key)
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            status: Status.OK,
                            customProtocol: result,
                        });
                    },
                    error => {
                        ctx.patchState({
                            customProtocol: null,
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            )
    }

    @Action(LoadProtocolPreference)
    loadProtocolPreference(ctx: StateContext<CustomProtocolIndexStateModel>, action: LoadProtocolPreference) {
        const state = ctx.getState();

        ctx.patchState({
            status: Status.LOADING,
        })

        const filter = (new FilterBuilder());

        filter.setQuery('ownerOrgKey', action.orgKey);

        return this._protocol_preferencesService.query(filter.get())
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            preferences: result.data,
                        })

                        return forkJoin(
                            ctx.dispatch(new LoadCustomProtocolIndex),
                            ctx.dispatch(new SetOrganizationContext(action.orgKey)),
                        );
                    },
                    error => {
                        ctx.patchState({
                            data: state.data ? state.data : null,
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            )
    }

    @Action(AddProtocolPreference)
    addProtocolPreference(ctx: StateContext<CustomProtocolIndexStateModel>, action: AddProtocolPreference) {
        let request: Observable<OrganizationProtocolPreference>;

        const state = ctx.getState();

        let preference: Partial<OrganizationProtocolPreference> = state.preferences.find(preference => {
            return preference.protocolId === action.protocol.id && preference.ownerOrgKey === action.orgKey
        });

        if (!preference) {
            preference = {
                ownerOrgKey: action.orgKey,
                protocolId: action.protocol.id,
            }
        }

        preference = {
            ...preference,
            favourite: action.protocol.favourite || false,
            hidden: action.protocol.hidden || false,
        }

        if (!preference.key) request = this._protocol_preferencesService.create(preference);
        else request = this._protocol_preferencesService.update(preference.key, preference);

        return request.pipe(
            tap(
                result => {
                    ctx.patchState({
                        status: Status.OK,
                    });

                    ctx.dispatch(new LoadProtocolPreference(action.orgKey))
                },
                error => {
                    ctx.patchState({
                        customProtocol: null,
                        status: translateCommonErrorStatus(error)
                    });
                }
            )
        );

    }

    @Action(AddPreferenceWithoutProtocolId)
    addPreferenceWithoutProtocolId(ctx: StateContext<CustomProtocolIndexStateModel>, action: AddPreferenceWithoutProtocolId) {
        let request: Observable<OrganizationProtocolPreference>;

        const state = ctx.getState();

        const preference = action.preference;

        if (!preference.key) request = this._protocol_preferencesService.create(preference);
        else request = this._protocol_preferencesService.update(preference.key, preference);

        return request.pipe(
            tap(
                result => {
                    ctx.patchState({
                        status: Status.OK,
                    });

                    ctx.dispatch(new LoadProtocolPreference(state.orgKey));
                },
                error => {
                    ctx.patchState({
                        customProtocol: null,
                        status: translateCommonErrorStatus(error)
                    });
                }
            )
        );

    }

    @Action(SetCustomProtocolIndexSelected)
    setCustomProtocolIndexSelected(ctx: StateContext<CustomProtocolIndexStateModel>, action: SetCustomProtocolIndexSelected) {
        const state = ctx.getState();

        const selectedData = action.ids.map(id => {
            let protocol = state.data.find(proto => proto.id === id);
            if (protocol) return protocol;
            return state.selectedData.find(proto => proto.id === id);
        });

        ctx.patchState({
            selectedIds: action.ids,
            selectedData
        });
    }

    private paginateProtocols(state: CustomProtocolIndexStateModel, protocols: Protocol[]): Protocol[] {
        let start = (state.page.index * state.page.size);
        let end = state.page.size * (state.page.index + 1);

        if (end > protocols.length) end = protocols.length;
        if (start > end && start != 0) start = (state.page.index - 1) * state.page.size;

        return protocols.slice(start, end);
    }

    private sortStringColumn(state: CustomProtocolIndexStateModel, protocols: Protocol[]): Protocol[] {
        return protocols.sort((a, b) => {
            if (state.sort.order === 'asc') return a.title.localeCompare(b.title);
            else return b.title.localeCompare(a.title);
        });
    }

    private sortTimestampColumn(state: CustomProtocolIndexStateModel, protocols: Protocol[]): Protocol[] {
        const column = state.sort.column;
        const isAsc = state.sort.order === 'asc';
        return protocols.sort((a, b) => {
            const dateA = a[column] !== undefined ? moment(a[column]) : null;
            const dateB = b[column] !== undefined ? moment(b[column]) : null;

            if (dateA === null && dateB === null) return 0;
            if (dateA === null) return isAsc ? -1 : 1;
            if (dateB === null) return isAsc ? 1 : -1;

            return isAsc
                ? dateA.diff(dateB)
                : dateB.diff(dateA);
        });
    }

    private sortCountColumn(state: CustomProtocolIndexStateModel, protocols: Protocol[]): Protocol[] {
        const column = state.sort.column;
        return protocols.sort((a, b) => {
            if (state.sort.order === 'asc') return a[column].length - b[column].length
            else return b[column].length - a[column].length
        });
    }

    private storeFilter(ctx: StateContext<CustomProtocolIndexStateModel>) {
        const state = ctx.getState();

        this._storage.set(`${state.orgKey}_custom_protocol_index_page`, state.page);
        this._storage.set(`${state.orgKey}_custom_protocol_index_sort`, state.sort);
        this._storage.set(`${state.orgKey}_custom_protocol_index_query`, state.query);

    }

    private restoreFilter(ctx: StateContext<CustomProtocolIndexStateModel>) {

        const state = ctx.getState();

        const page = this._storage.get(`${state.orgKey}_custom_protocol_index_page`);
        const sort = this._storage.get(`${state.orgKey}_custom_protocol_index_sort`);
        const query = this._storage.get(`${state.orgKey}_custom_protocol_index_query`);

        ctx.patchState({
            sort: sort || DEFAULTS.sort,
            page: page || DEFAULTS.page,
            query: query || DEFAULTS.query,
        });
    }


}



